import React, {useState, useEffect, useMemo, useRef, useCallback, forwardRef, useImperativeHandle} from 'react';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebase-config"
import { AppCTX } from '../data/AppContext';
import { Polygon, OverlayView } from "@react-google-maps/api";
import { PolygonLayer, ScatterplotLayer, LineLayer, IconLayer } from '@deck.gl/layers';
import TeamAssignmentLayers from './TeamAssignmentLayers';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import isEqual from 'lodash.isequal';

const saveAssignAreaButtonPosition = 6
const continueOrDeleteButtonPosition = 4

// Drawing modes
const DRAWING_MODES = {
  VIEW: 'view',
  DRAW_POLYGON: 'drawPolygon',
  EDIT: 'edit'
};

function AreaManager ({zoomLevel, areaManagerActive, setAreaManagerActive, onDrawingLayersChange}, ref) {
    const { user, map, teamData, setTeamData, setNewProspect, newProspect, updateTeamData, setStatus } = React.useContext(AppCTX);

    const [open, setOpen] = useState(false)
    const [currentPolygon, setCurrentPolygon] = useState(null)
    // Remove transparentPolygons state and related logic
    // const [transparentPolygons, setTransparentPolygons] = useState(false)
    const [showAreaManagerbutton, setShowAreaManagerbutton] = useState(true)
    const [assignAreaTo, setAssignAreaTo] = useState(null)
    const finalizeAreaBtn = useRef(null)
    const areaManagerBtn = useRef(null)
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showUserSelect, setShowUserSelect] = useState(false);
    const [pendingPolygon, setPendingPolygon] = useState(null);

    // Drawing state (migrated from NebulaDrawingManager)
    const [drawingPoints, setDrawingPoints] = useState([]);
    const [isDrawing, setIsDrawing] = useState(false);
    const [mode, setMode] = useState(DRAWING_MODES.VIEW);
    const [selectedFeature, setSelectedFeature] = useState(null);

    // Complete polygon drawing
    const completePolygon = useCallback(() => {
        if (drawingPoints.length >= 3) {
            const coordinates = drawingPoints.map(([lng, lat]) => ({ lng, lat }));
            
            // Trigger the assign modal
            setShowAssignModal(true);
            setPendingPolygon({
                coordinates,
                properties: {
                    id: `polygon-${Date.now()}`,
                    name: 'New Area',
                    color: '#ff0000',
                    fillColor: '#ff0000',
                    strokeColor: '#ff0000',
                    strokeWidth: 2,
                    fillOpacity: 0.3
                }
            });
            
            setDrawingPoints([]);
            setIsDrawing(false);
            setAreaManagerActive(false);
            setMode(DRAWING_MODES.VIEW);
        }
    }, [drawingPoints, setAreaManagerActive]);

    // Expose map click handler for parent component
    const handleMapClickFromParent = useCallback((mapEvent) => {
        console.log('[AreaManager] Map click received - mode:', mode, 'latLng:', mapEvent.detail?.latLng);
        if (mode === DRAWING_MODES.DRAW_POLYGON && mapEvent.detail?.latLng) {
            const coordinate = [mapEvent.detail.latLng.lng, mapEvent.detail.latLng.lat];
            console.log('[AreaManager] Adding coordinate:', coordinate);
            setDrawingPoints(prev => [...prev, coordinate]);
        }
    }, [mode]);

    // Expose double-click handler for parent component
    const handleDoubleClickFromParent = useCallback(() => {
        if (mode === DRAWING_MODES.DRAW_POLYGON && drawingPoints.length >= 3) {
            completePolygon();
        }
    }, [mode, drawingPoints, completePolygon]);

    // Expose methods through ref
    useImperativeHandle(ref, () => ({
        handleMapClickFromParent,
        handleDoubleClickFromParent
    }), [handleMapClickFromParent, handleDoubleClickFromParent]);

      useEffect(() => {
    // No need to set transparentPolygons anymore
  }, [zoomLevel]);

    // Handle drawing mode changes
    const handleModeChange = useCallback((newMode) => {
        console.log('[AreaManager] Mode change requested:', newMode, 'current mode:', mode);
        setMode(newMode);
        
        if (newMode === DRAWING_MODES.VIEW) {
            setAreaManagerActive(false);
            setIsDrawing(false);
            setDrawingPoints([]);
            setSelectedFeature(null);
        } else if (newMode === DRAWING_MODES.DRAW_POLYGON) {
            setAreaManagerActive(true);
            setIsDrawing(true);
            setDrawingPoints([]);
            setSelectedFeature(null);
        } else if (newMode === DRAWING_MODES.EDIT) {
            setAreaManagerActive(true);
            setIsDrawing(false);
            setDrawingPoints([]);
        }
    }, [setAreaManagerActive, mode]);

    // Handle map clicks for drawing
    const handleMapClick = useCallback((event) => {
        if (mode !== DRAWING_MODES.DRAW_POLYGON) return;
        
        const { coordinate } = event;
        if (coordinate) {
            setDrawingPoints(prev => [...prev, coordinate]);
        }
    }, [mode]);

    // Remove last point (Back button)
    const handleBack = useCallback(() => {
        setDrawingPoints(prev => prev.length > 0 ? prev.slice(0, -1) : prev);
    }, []);

    // SVG data URL for dashed red square
    const dashedRedSquareIcon =
        'data:image/svg+xml;utf8,<svg width="32" height="32" xmlns="http://www.w3.org/2000/svg"><rect x="4" y="4" width="24" height="24" fill="none" stroke="red" stroke-width="3" stroke-dasharray="5,4"/></svg>';

    // Memoize calculatePolygonCenter for performance
    const calculatePolygonCenter = useCallback((vertices) => {
        if (vertices.length === 0) {
          return null;
        }
        // Calculate the average latitude and longitude
        const avgLat = vertices.reduce((sum, vertex) => sum + vertex.lat, 0) / vertices.length;
        const avgLng = vertices.reduce((sum, vertex) => sum + vertex.lng, 0) / vertices.length;
        return { lat: avgLat, lng: avgLng };
    }, []);

    // Helper for stable memoization with deep equality
    function useStableMemo(factory, deps) {
      const ref = useRef();
      const value = factory();
      if (!ref.current || !isEqual(ref.current, value)) {
        ref.current = value;
      }
      return ref.current;
    }

    // Stable memoized drawing points
    const stableDrawingPoints = useStableMemo(() => drawingPoints, [drawingPoints]);

    // Only compute drawing layers when in drawing mode, with stable references
    const drawingPolygonLayer = useMemo(() => {
      if (mode !== DRAWING_MODES.DRAW_POLYGON || stableDrawingPoints.length < 3) return null;
      const polygonPoints = [...stableDrawingPoints];
      if (polygonPoints.length > 0) {
        polygonPoints.push(polygonPoints[0]);
      }
      // Make the polygon fill and border solid red and visible
      return new PolygonLayer({
        id: 'drawing-polygon',
        data: [{
          polygon: [polygonPoints],
          properties: { id: 'drawing-polygon', isDrawing: true }
        }],
        getPolygon: d => d.polygon,
        getFillColor: [255, 0, 0, 180],      // Solid red fill, mostly opaque
        getLineColor: [255, 0, 0, 255],      // Solid red border
        lineWidthMinPixels: 3,
        pickable: true,
        filled: true,
        stroked: true,
        opacity: 1
      });
    }, [mode, stableDrawingPoints, zoomLevel]);

    // Get team assignment layers
    const teamAssignmentLayers = TeamAssignmentLayers({ teamData, zoomLevel });

    const drawingLinesLayer = useMemo(() => {
      if (mode !== DRAWING_MODES.DRAW_POLYGON || stableDrawingPoints.length < 2) return null;
      const lines = [];
      for (let i = 0; i < stableDrawingPoints.length - 1; i++) {
        lines.push({
          path: [stableDrawingPoints[i], stableDrawingPoints[i + 1]],
          properties: { index: i }
        });
      }
      if (stableDrawingPoints.length >= 3) {
        lines.push({
          path: [stableDrawingPoints[stableDrawingPoints.length - 1], stableDrawingPoints[0]],
          properties: { index: stableDrawingPoints.length - 1, isClosing: true }
        });
      }
      const isTransparent = zoomLevel >= 15;
      const lineAlpha = isTransparent ? 150 : 255;
      const closingLineAlpha = isTransparent ? 180 : 255;
      return new LineLayer({
        id: 'drawing-lines',
        data: lines,
        getPath: d => d.path,
        getColor: d => d.properties.isClosing ? [255, 255, 0, closingLineAlpha] : [255, 255, 255, lineAlpha],
        getWidth: d => d.properties.isClosing ? 4 : 3,
        pickable: false
      });
    }, [mode, stableDrawingPoints, zoomLevel]);

    const allPointsIconLayer = useMemo(() => {
      if (mode !== DRAWING_MODES.DRAW_POLYGON || stableDrawingPoints.length === 0) return null;
      const isTransparent = zoomLevel >= 15;
      const iconSize = isTransparent ? 24 : 32;
      const opacity = isTransparent ? 0.7 : 1.0;
      return new IconLayer({
        id: 'drawing-all-points-icon',
        data: stableDrawingPoints.map((point, index) => ({ position: point, index })),
        getPosition: d => d.position,
        getIcon: () => 'dashedRedSquare',
        iconAtlas: dashedRedSquareIcon,
        iconMapping: {
          dashedRedSquare: { x: 0, y: 0, width: 32, height: 32, mask: false }
        },
        sizeScale: opacity,
        getSize: iconSize,
        pickable: false
      });
    }, [mode, stableDrawingPoints, zoomLevel]);

    // Combine all drawing layers with stable reference
    const drawingLayers = useStableMemo(() => {
      const layers = [];
      
      // Always include team assignment layers (even when not drawing)
      if (teamAssignmentLayers && teamAssignmentLayers.length > 0) {
        layers.push(...teamAssignmentLayers);
      }
      
      // Add drawing layers only when in drawing mode
      if (mode === DRAWING_MODES.DRAW_POLYGON) {
        if (drawingLinesLayer) layers.push(drawingLinesLayer);
        if (drawingPolygonLayer) layers.push(drawingPolygonLayer);
        if (allPointsIconLayer) layers.push(allPointsIconLayer);
      }
      
      return layers;
    }, [mode, drawingLinesLayer, drawingPolygonLayer, allPointsIconLayer, teamAssignmentLayers, zoomLevel]);

    // Only notify parent if drawingLayers actually changed
    const prevDrawingLayersRef = useRef();
    useEffect(() => {
      if (!isEqual(prevDrawingLayersRef.current, drawingLayers)) {
        prevDrawingLayersRef.current = drawingLayers;
        if (onDrawingLayersChange) {
          onDrawingLayersChange(drawingLayers);
        }
      }
    }, [drawingLayers, onDrawingLayersChange]);

    // Only update drawingPoints if new value is different
    const setDrawingPointsIfChanged = useCallback((updater) => {
      setDrawingPoints(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        return isEqual(prev, next) ? prev : next;
      });
    }, []);

    // Use setDrawingPointsIfChanged everywhere instead of setDrawingPoints
    // (You may need to update all setDrawingPoints calls in the file to use setDrawingPointsIfChanged)

    const finalizeArea = () =>{
        const paths = currentPolygon.getPath().getArray();
        let pathArr = []
        paths.forEach ((path)=>{
            const coords = {lat: path.lat(), lng: path.lng()}
            pathArr.push(coords)
        })
        // we are now updating the team data locally first
        if(teamData.hasOwnProperty('areas')){
            console.log("previous team areas", teamData.areas)
            let update = {...teamData.areas, [assignAreaTo]: pathArr}
            update = {...teamData, areas: update}

            console.log("new team areas", update)
            setTeamData(update)
            updateTeamData(update)
            deleteCurrentPolygon()
            setShowAreaManagerbutton(true)
        }
        else{
            console.log("that didnt work", update)
            let update = {[assignAreaTo]: pathArr}
            update = {...teamData, areas: update}
            setTeamData(update)
            updateTeamData(update)
            deleteCurrentPolygon()
            setShowAreaManagerbutton(true)
        }
    }

    const handleRedraw = () => {
        if (pendingPolygon) {
            setPendingPolygon(null);
            setShowAssignModal(false);
            setShowAreaManagerbutton(false);
            setAreaManagerActive(true);
            // Switch to drawing mode
            handleModeChange(DRAWING_MODES.DRAW_POLYGON);
        }
    };

    const handleAssignArea = () => {
        setShowAssignModal(false);
        setShowUserSelect(true);
    };

    const handleUserSelect = (name) => {
        setAssignAreaTo(name);
        finalizeAreaWithUser(name);
        setShowUserSelect(false);
        setShowAreaManagerbutton(true);
        setAreaManagerActive(false);
        // Switch back to view mode
        handleModeChange(DRAWING_MODES.VIEW);
    };

    const finalizeAreaWithUser = (name) => {
        console.log('[DEBUG] finalizeAreaWithUser triggered for:', name);
        const pathArr = pendingPolygon.coordinates;
        let update;
        if (teamData.hasOwnProperty('areas')) {
            console.log('[DEBUG] Old area coords for', name, teamData.areas[name]);
            update = { ...teamData.areas, [name]: pathArr };
            update = { ...teamData, areas: update };
            console.log('[DEBUG] New area coords for', name, pathArr);
        } else {
            console.log('[DEBUG] No previous areas, creating new for', name);
            update = { [name]: pathArr };
            update = { ...teamData, areas: update };
            console.log('[DEBUG] New area coords for', name, pathArr);
        }
        setTeamData(update);
        updateTeamData(update);
        setPendingPolygon(null);
    };

    const handleAreaAssignment = () => {
        if(areaManagerActive){
            updateTeamData({...teamData})
            setAreaManagerActive(false);
            handleModeChange(DRAWING_MODES.VIEW);
        }else{
            setNewProspect({...newProspect,coords:false})
            setAreaManagerActive(true);
            handleModeChange(DRAWING_MODES.DRAW_POLYGON);
        }
    }

    const deleteCurrentPolygon = () => {
        currentPolygon.setMap(null)
        setCurrentPolygon(null)
        setShowAreaManagerbutton(true)
    }

    const handlePanToArea = async () => {
        // Check if teamData and user are available
        if (teamData?.areas && user.uid) {
            // Get the user's assigned area coordinates from teamData.areas
            const userArea = teamData.areas[user.data.name];
            // Check if userArea is defined and contains at least one point
            if (userArea && userArea.length > 0) {
                const bounds = new window.google.maps.LatLngBounds();
                userArea.forEach(coords => {
                    bounds.extend(new window.google.maps.LatLng(coords.lat, coords.lng));
                });
                // Fit the map to the LatLngBounds, ensuring all points are visible
                map.fitBounds(bounds);
                // Calculate center of polygon
                const center = calculatePolygonCenter(userArea);
                if (center && process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
                    try {
                        const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${center.lat},${center.lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`);
                        const data = await response.json();
                        if (data.status === 'OK' && data.results && data.results[0]) {
                            const address = data.results[0].formatted_address;
                            setStatus && setStatus({ notificationType: 'info', message: `Travel to ${address} to get to your area.` });
                        } else {
                            setStatus && setStatus({ notificationType: 'info', message: 'Could not find address for this area.' });
                        }
                    } catch (e) {
                        setStatus && setStatus({ notificationType: 'error', message: 'Failed to get address: ' + e.message });
                    }
                } else {
                    setStatus && setStatus({ notificationType: 'error', message: 'No center found or API key missing.' });
                }
            }
        }
    }

    // Helper to check permissions
    const canManageAreas = user?.data?.memberType === 'superuser' || user?.data?.memberType === 'closer';
    const isSetter = user?.data?.memberType === 'setter';

    // Find the current member in teamData.members
    const currentMember = teamData?.members?.find(
      (member) => member.name === user?.data?.name
    );

    // Check permissions - use UID for superuser check
    const SUPERUSER_UID = process.env.REACT_APP_SUPERUSER;
    const canAssignArea =
      user?.uid === SUPERUSER_UID ||
      (currentMember && currentMember.areaManager === true);

    if(teamData==null)return(<></>)
    else return(
        <>
            {canAssignArea ? (
                areaManagerActive && !showAssignModal && !showUserSelect ? (
                    <div
                        style={{
                            position: 'absolute',
                            top: 72,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 1000,
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 12,
                            justifyContent: 'space-between',
                            background: 'rgba(30,30,30,0.96)',
                            borderRadius: 14,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.22)',
                            padding: '10px 14px',
                            minWidth: 220,
                            maxWidth: '90vw',
                            border: '1.5px solid #222',
                        }}
                    >
                        <Button
                            sx={{
                                color: '#fff',
                                background: '#888',
                                borderRadius: '10px',
                                fontWeight: 600,
                                fontSize: '0.92rem',
                                letterSpacing: 0.5,
                                textTransform: 'uppercase',
                                px: 1.5,
                                py: 0.5,
                                boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                                marginRight: 4,
                                minWidth: 36,
                                height: 36,
                                transition: 'background 0.2s',
                                '&:hover': { background: '#555' },
                                '&.Mui-disabled': { background: '#444', color: '#bbb' },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            variant="contained"
                            onClick={handleBack}
                            disabled={!drawingPoints || drawingPoints.length === 0}
                        >
                            <ArrowBackIcon />
                        </Button>
                        <Button
                            sx={{
                                color: '#fff',
                                background: '#d32f2f',
                                borderRadius: '10px',
                                fontWeight: 700,
                                fontSize: '1rem',
                                letterSpacing: 0.5,
                                textTransform: 'uppercase',
                                px: 2,
                                py: 0.7,
                                boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                                minWidth: 36,
                                height: 36,
                                transition: 'background 0.2s',
                                '&:hover': { background: '#b71c1c' },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            variant="contained"
                            onClick={() => {
                                setAreaManagerActive(false);
                                handleModeChange(DRAWING_MODES.VIEW);
                            }}
                        >
                            <CloseIcon />
                        </Button>
                        {isDrawing && drawingPoints && drawingPoints.length >= 3 && (
                            <Button
                                sx={{
                                    color: '#fff',
                                    background: '#4caf50',
                                    borderRadius: '10px',
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    letterSpacing: 0.5,
                                    textTransform: 'uppercase',
                                    px: 2,
                                    py: 0.7,
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                                    minWidth: 36,
                                    height: 36,
                                    transition: 'background 0.2s',
                                    '&:hover': { background: '#388e3c' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                variant="contained"
                                onClick={completePolygon}
                            >
                                <CheckIcon />
                            </Button>
                        )}
                    </div>
                ) : (
                    canAssignArea && (
                        <Button
                            id="area-manager-btn"
                            sx={{
                                position: 'absolute',
                                bottom: 32,
                                right: 32,
                                width: 120,
                                height: 48,
                                borderRadius: '24px',
                                background: '#222',
                                color: '#fff',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                                fontWeight: 700,
                                fontSize: '1rem',
                                zIndex: 10,
                                letterSpacing: 1,
                                transition: 'background 0.2s',
                                textTransform: 'uppercase',
                                textAlign: 'center',
                                justifyContent: 'center',
                                alignItems: 'center',
                                display: 'flex',
                                '&:hover': {
                                    background: '#333',
                                },
                            }}
                            variant="contained"
                            onClick={handleAreaAssignment}
                        >
                            Assign
                        </Button>
                    )
                )
            ) : (
                <Button
                    id="area-manager-btn"
                    sx={{
                        position: 'absolute',
                        bottom: 32,
                        right: 32,
                        width: 120,
                        height: 48,
                        borderRadius: '24px',
                        background: '#222',
                        color: '#fff',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                        fontWeight: 700,
                        fontSize: '1rem',
                        zIndex: 10,
                        letterSpacing: 1,
                        transition: 'background 0.2s',
                        textTransform: 'uppercase',
                        textAlign: 'center',
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: 'flex',
                        '&:hover': {
                            background: '#333',
                        },
                    }}
                    variant="contained"
                    onClick={handlePanToArea}
                >
                    My Area
                </Button>
            )}
            
            {/* Assign/Redraw Modal */}
            {showAssignModal && (
                <Dialog 
                    onClose={() => setShowAssignModal(false)} 
                    open={showAssignModal}
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            p: 2,
                            background: '#333',
                            boxShadow: 8,
                            minWidth: 340,
                            textAlign: 'center',
                        }
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 700, fontSize: 24, color: '#fff', mb: 2 }}>
                        What would you like to do?
                    </DialogTitle>
                    <List sx={{ pt: 0 }}>
                        <ListItem disableGutters sx={{ mb: 2 }}>
                            <Button 
                                variant="contained" 
                                onClick={handleAssignArea} 
                                fullWidth
                                sx={{
                                    fontWeight: 700,
                                    fontSize: 18,
                                    py: 1.5,
                                    borderRadius: 2,
                                    background: '#fff',
                                    color: '#222',
                                    boxShadow: 2,
                                    '&:hover': { background: '#eee' },
                                }}
                            >
                                ASSIGN AREA
                            </Button>
                        </ListItem>
                        <ListItem disableGutters>
                            <Button 
                                variant="outlined" 
                                color="error" 
                                onClick={handleRedraw} 
                                fullWidth
                                sx={{
                                    fontWeight: 700,
                                    fontSize: 18,
                                    py: 1.5,
                                    borderRadius: 2,
                                    borderWidth: 2,
                                    borderColor: '#ff5252',
                                    color: '#ff5252',
                                    background: 'transparent',
                                    '&:hover': { background: '#2a2a2a', borderColor: '#ff5252' },
                                }}
                            >
                                REDRAW
                            </Button>
                        </ListItem>
                    </List>
                </Dialog>
            )}
            {/* User Select Modal */}
            {showUserSelect && (
                <Dialog 
                    onClose={() => setShowUserSelect(false)} 
                    open={showUserSelect}
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            p: 2,
                            background: '#333',
                            boxShadow: 8,
                            minWidth: 340,
                            textAlign: 'center',
                        }
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 700, fontSize: 22, color: '#fff', mb: 2 }}>
                        Select a team member to assign this area
                    </DialogTitle>
                    <List sx={{ pt: 0 }}>
                        {teamData?.members?.map((member, idx) => (
                            <ListItem disableGutters key={idx} sx={{ mb: 1 }}>
                                <ListItemButton onClick={() => handleUserSelect(member.name)} sx={{ borderRadius: 2 }}>
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: member.color, width: 32, height: 32, fontWeight: 700, color: '#fff' }}>
                                            {/* Optionally show initials or blank */}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText primary={member.name} primaryTypographyProps={{ sx: { color: '#fff', fontWeight: 500 } }} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Dialog>
            )}
        </>
    )
}

export default forwardRef(AreaManager);
